import { max, min } from "@clockwise/client-commons/src/constants/time-slot";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { ECDatePicker } from "../molecules/ECDatePicker";
import {
  evaluateTimesFromEndTimeChange,
  evaluateTimesFromStartTimeChange,
  isValidTimeInput,
  isValidTimeRange,
} from "../utils/eventCardTime.util";

import { isAllDayBasedOnTimes } from "../../web-app-calendar/hooks/useEditExistingEvent.util";
import { EventElementsWrap } from "../atoms/EventElementsWrap";
import { useReadEventTimeDetails, useUpdateEventTimeDetails } from "../hooks/EventCardContext";
import { ECTimePicker } from "../molecules/ECTimePicker";

const getDisplayTime = (time: DateTime) => {
  return time.toFormat("h:mma");
};

export const Time = ({
  readOnly,
  hasDateTimeChanged,
  updateFlexToggle,
  timesHaveChanged = false,
  hideUIWrapper = false,
}: {
  readOnly: boolean;
  hasDateTimeChanged?: boolean;
  updateFlexToggle?: () => void;
  timesHaveChanged?: boolean;
  hideUIWrapper?: boolean;
}) => {
  const { startTime, endTime, isAllDay, timeError } = useReadEventTimeDetails();
  const { setStartTime, setEndTime, setIsAllDay, setTimeError } = useUpdateEventTimeDetails();

  // Determine if new times make the event "all day"
  const isAllDayFromTimes = isAllDayBasedOnTimes(startTime, endTime);

  useEffect(() => {
    setIsAllDay(isAllDayFromTimes);
  }, [isAllDayFromTimes, setIsAllDay]);

  const validateAndCallback = (newStart: DateTime, newEnd: DateTime) => {
    if (isValidTimeRange(newStart, newEnd)) {
      setTimeError(null);
      setStartTime(newStart);
      setEndTime(newEnd);
      if (
        newStart.toMillis() !== startTime.toMillis() ||
        newEnd.toMillis() !== endTime.toMillis()
      ) {
        updateFlexToggle?.();
      }
    } else {
      setTimeError("Start time must be before end time.");
    }
  };

  return hideUIWrapper ? (
    <ECTime
      startTime={startTime}
      endTime={endTime}
      readOnly={readOnly}
      onChange={validateAndCallback}
      isAllDay={isAllDay}
      hasDateTimeChanged={hasDateTimeChanged}
      timeError={timeError}
      setTimeError={setTimeError}
    />
  ) : (
    <EventElementsWrap name="time" showChanges={timesHaveChanged}>
      <ECTime
        startTime={startTime}
        endTime={endTime}
        readOnly={readOnly}
        onChange={validateAndCallback}
        isAllDay={isAllDay}
        hasDateTimeChanged={hasDateTimeChanged}
        timeError={timeError}
        setTimeError={setTimeError}
      />
    </EventElementsWrap>
  );
};

export const ECTime = ({
  readOnly,
  startTime,
  endTime,
  onChange,
  isAllDay,
  timeError,
  setTimeError,
}: {
  readOnly: boolean;
  startTime: DateTime;
  endTime: DateTime;
  onChange: (start: DateTime, end: DateTime) => void;
  hasDateTimeChanged?: boolean;
  isAllDay: boolean;
  timeError: string | null;
  setTimeError: (error: string | null) => void;
}) => {
  const isStartAndEndSameDay = startTime.hasSame(endTime, "day");

  const onChangeStartDate = (iso: DateTime) => {
    const newStartDateValues = {
      year: iso.year,
      month: iso.month,
      day: iso.day,
    };
    const startDateTime = startTime.set(newStartDateValues);

    if (isAllDay) {
      const newEndDate = startDateTime.plus({ days: 1 });
      const newEndDateValues = {
        year: newEndDate.year,
        month: newEndDate.month,
        day: newEndDate.day,
      };
      const endDateTime = endTime.set(newEndDateValues);
      onChange(startDateTime, endDateTime);
    } else if (isStartAndEndSameDay) {
      const endDateTime = endTime.set(newStartDateValues);
      onChange(startDateTime, endDateTime);
    } else {
      onChange(startDateTime, endTime);
    }
  };

  const onChangeStartTime = (newStartTime: string) => {
    if (!isValidTimeInput(newStartTime)) {
      return setTimeError("Invalid start time");
    }

    const { start, end } = evaluateTimesFromStartTimeChange(newStartTime, startTime, endTime);

    setTimeError(null);
    onChange(start, end);
  };

  const onChangeEndTime = (newEndTime: string) => {
    if (!isValidTimeInput(newEndTime)) {
      return setTimeError("Invalid end time");
    }
    const { start, end } = evaluateTimesFromEndTimeChange(newEndTime, startTime, endTime);

    setTimeError(null);
    onChange(start, end);
  };

  return (
    <div className="cw-flex cw-flex-col cw-gap-2 cw-flex-1">
      <div className="cw-flex cw-flex-1 cw-items-center cw-gap-2">
        <span className="cw-font-medium cw-text-subtle cw-text-12 cw-text-start cw-w-9">
          {isAllDay ? "Date" : "Start"}
        </span>
        <ECDatePicker
          date={startTime}
          readOnly={readOnly}
          onChange={onChangeStartDate}
          error={timeError}
        />
        {!isAllDay && (
          <ECTimePicker
            value={getDisplayTime(startTime)}
            onChange={onChangeStartTime}
            slots={Object.keys(min).map((timeslot) => min[timeslot].toUpperCase())}
            readOnly={readOnly}
            error={timeError}
          />
        )}
      </div>
      {!isAllDay && (
        <div className="cw-flex cw-flex-1 cw-items-center cw-gap-2">
          <span className="cw-font-medium cw-text-subtle cw-text-12 cw-text-start cw-w-9">End</span>
          <ECDatePicker
            date={startTime}
            readOnly={true}
            onChange={onChangeStartDate}
            error={timeError}
          />
          <ECTimePicker
            value={getDisplayTime(endTime)}
            onChange={onChangeEndTime}
            slots={Object.keys(max).map((timeslot) => max[timeslot].toUpperCase())}
            readOnly={readOnly}
            error={timeError}
          />
        </div>
      )}

      {timeError && (
        <div className="cw-text-destructive cw-text-13 cw-font-normal cw-text-center">
          {timeError}
        </div>
      )}
    </div>
  );
};
